/** Represents a supported country with its ISO 3166-1 alpha-2 code and localization label key. */
export interface ICountryData {
  code: string;
  labelKey: string;
  /**
   * Billing currency for the country's local currency, as a lowercase Stripe/BillingCurrency code (e.g. 'usd', 'cad'). Used to localize plan pricing and to settle checkout in the visitor's
   * currency.
   */
  currency: string;
}

/** @returns The full list of supported countries. */
export function GetSupportedCountriesList(): ICountryData[] {
  return supportedCountriesData;
}

/**
 * Retrieves data for a given country code.
 *
 * @param code - ISO 3166-1 alpha-2 country code (e.g., 'US').
 * @returns The country data object if found, undefined otherwise.
 */
export function GetSupportedCountryData(code: string): ICountryData | undefined {
  return supportedCountriesData.find((c) => c.code === code);
}

/** Curated list of supported countries for business address, onboarding, and signup forms. ISO 3166-1 alpha-2 codes paired with their localization label key. */
const supportedCountriesData: ICountryData[] = [
  { code: 'US', labelKey: 'CountryUnitedStates', currency: 'usd' },
  { code: 'CA', labelKey: 'CountryCanada', currency: 'cad' }
];

/**
 * English baseline display names for every supported country, keyed by the same `labelKey` that {@link GetSupportedCountriesList} entries carry. Each frontend app spreads this map into its
 * `Localized` object so the onboarding wizard, the business profile, and the signup card all render the identical human-friendly country name (instead of a raw ISO code). Keeping the single
 * source here guarantees the lobby and portal never drift apart.
 */
export const CountryLabels: Record<string, string> = {
  CountryUnitedStates: 'United States',
  CountryCanada: 'Canada'
};
